import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isKioskMode } from '../../../selectors/ui';
import {
  POSITION_POPUP_BEFORE_REGISTRATION,
  POSITION_EMBEDDED_IN_REGISTRATION,
  UPSELL_TYPE_PRODUCT,
  DISPLAY_TYPE_LIST,
  genSubReducerKey,
} from '../reducers/upsells';
import EventUpsell from '../components/EventUpsell';
import ProductUpsell from '../components/ProductUpsell';
import { createLoadingSelector } from '../../../selectors/helpers';
import Translation from '@s/Translation';

class UpsellsContainer extends Component {
  getUpsells = () => {
    return this.props.upsells.filter((upsell, index) => {
      return upsell?.upsell?.position === this.props.position;
    });
  };

  groupUpsellsByIdAndSort = (upsellsList) => {
    const grouped = {};

    // group
    if (Array.isArray(upsellsList)) {
      upsellsList.forEach((upsell) => {
        const upsellId = upsell?.upsell?.id;
        if (upsellId) {
          if (grouped[upsellId] === undefined) {
            grouped[upsellId] = [];
          }
          grouped[upsellId].push(upsell);
        }
      });
    }

    // sort
    Object.keys(grouped).forEach((upsellId) => {
      const upsells = [...grouped[upsellId]];
      upsells.sort((a, b) => {
        const aSortPos = a?.upsell?.sort_position ? +a.upsell.sort_position : 0;
        const bSortPos = b?.upsell?.sort_position ? +b.upsell.sort_position : 0;
        if (aSortPos == bSortPos) {
          return 0;
        }
        return aSortPos - bSortPos;
      });
      grouped[upsellId] = upsells;
    });
    return grouped;
  };

  filterUpsellsToMatchEvents = (upsells, events) => {
    const eventSet = new Set(events);
    return upsells.filter((u) => u.upsell?.listings_events?.some((id) => eventSet.has(+id)) ?? false);
  };

  render() {
    const upsells = this.getUpsells();

    if (upsells.length < 1) {
      return null;
    }

    // only allow upsells that matches tickets in cart
    let allEventsFromTickets = [];
    this.props.tickets.forEach(t => {
      allEventsFromTickets.push(t.event_id);
      if (!isNaN(t.parent_event_id)) {
        allEventsFromTickets.push(t.parent_event_id);
      }
    });
    const eventsFromTickets = Array.from(new Set(allEventsFromTickets));

    const eventUpsells = upsells.filter((upsell) => +upsell?.upsell?.type !== UPSELL_TYPE_PRODUCT);
    const productUpsells = upsells.filter((upsell) => +upsell?.upsell?.type === UPSELL_TYPE_PRODUCT);
    const eventUpsellsByUpsellId = this.groupUpsellsByIdAndSort(
      this.filterUpsellsToMatchEvents(eventUpsells, eventsFromTickets),
    );
    const productUpsellsByUpsellId = this.groupUpsellsByIdAndSort(
      this.filterUpsellsToMatchEvents(productUpsells, eventsFromTickets),
    );

    return (
      <>
        {!!Object.keys(productUpsellsByUpsellId).length && (
          <div className="upsell__section__title upsell-section-title--product-embed">
            <h1><Translation>{'checkout__add_to_order'}</Translation></h1>
          </div>
        )}
        <div className="upsell-product__container">
          {!!Object.keys(productUpsellsByUpsellId).length && this.props.position !== POSITION_POPUP_BEFORE_REGISTRATION}
          {Object.values(productUpsellsByUpsellId).map((productUpsellsArray, index) => {
            const isList = productUpsellsArray[0]?.upsell?.display_type == DISPLAY_TYPE_LIST;
            return (
              <div
                key={index}
                className={`upsell-product-container__upsell ${
                  isList ? 'upsell--display-list' : 'upsell--display-grid'
                } ${
                  this.props.position !== POSITION_POPUP_BEFORE_REGISTRATION
                    ? 'upsell--position-embed'
                    : 'upsell--position-popup'
                }`}
              >
                {productUpsellsArray.map((upsellContent, index) => {
                  if (!upsellContent?.product_id || !+upsellContent?.upsell?.item_id) {
                    return null;
                  }

                  const key = genSubReducerKey(upsellContent.upsell.item_id, upsellContent.upsell.type);

                  return (
                    <section className="upsell__section upsell__section--product" key={index}>
                      <ProductUpsell
                        upsellId={upsellContent?.upsell?.id ? +upsellContent?.upsell?.id : 0}
                        itemId={+upsellContent?.upsell?.item_id}
                        productId={+upsellContent.product_id}
                        reducerKey={key}
                        canEdit={this.props.position !== POSITION_POPUP_BEFORE_REGISTRATION}
                        displayTypeList={upsellContent?.upsell?.display_type == DISPLAY_TYPE_LIST}
                        disabled={!!this.props.stepLoading}
                      />
                    </section>
                  );
                })}
              </div>
            );
          })}
        </div>

        {!!Object.keys(eventUpsellsByUpsellId).length && (
          <div className="upsell__section__title">
            <h3><Translation>{'checkout__recommended_events'}</Translation></h3>
          </div>
        )}
        {Object.values(eventUpsellsByUpsellId).map((eventUpsellGroup, groupIndex) => {
          return (
            <div key={groupIndex}>
              {eventUpsellGroup.map((upsellContent, index) => {
                if (!upsellContent?.event) {
                  return null;
                }

                const key = genSubReducerKey(upsellContent.upsell.event_map_id, upsellContent.upsell.type);

                return (
                  <section className="upsell__section upsell__section--event" key={index}>
                    <EventUpsell
                      upsellId={upsellContent?.upsell?.id ? +upsellContent?.upsell?.id : 0}
                      doesCartMatching={!!upsellContent?.upsell?.match}
                      hideOnMatchFail={!!upsellContent?.upsell?.hide_match_on_fail}
                      doesTimeslotMatching={
                        !!upsellContent?.upsell?.timeslot_match_exact ||
                        !!upsellContent?.upsell?.timeslot_match_next_available
                      }
                      cartMatch={upsellContent?.upsell_match}
                      timeslotMatch={upsellContent?.timeslot_match}
                      reducerKey={key}
                      imageSize={upsellContent?.upsell?.imageSize}
                      event={upsellContent?.event}
                      venueName={upsellContent?.venue?.name}
                      venueCity={upsellContent?.venue?.address?.city}
                      venueTimezone={upsellContent?.venue?.address?.timezone}
                      collapseDescription={!!upsellContent?.upsell?.descriptionCollapsed}
                      priceLevels={upsellContent?.price_levels}
                      bundles={upsellContent?.bundles}
                      products={upsellContent?.products}
                      canEdit={this.props.position !== POSITION_POPUP_BEFORE_REGISTRATION && !this.props.isKiosk} // hide the edit button for popup and kiosk
                      position={this.props.position}
                      canChangeMatchedTimeslot={!!upsellContent?.upsell?.can_change_matched_timeslot}
                      isKiosk={this.props.isKiosk}
                      disabled={!!this.props.stepLoading}
                    />
                  </section>
                );
              })}
            </div>
          );
        })}
      </>
    );
  }
}

const stepLoading = createLoadingSelector('CHECKOUT_STEP');
const mapStateToProps = (state) => {
  return {
    stepLoading: stepLoading(state),
    upsells: Array.isArray(state.upsells.upsells) ? state.upsells.upsells : [],
    tickets: Array.isArray(state.tickets.tickets) ? state.tickets.tickets : [],
    isKiosk: isKioskMode(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

UpsellsContainer.propTypes = {
  position: PropTypes.oneOf(Object.values([POSITION_POPUP_BEFORE_REGISTRATION, POSITION_EMBEDDED_IN_REGISTRATION]))
    .isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpsellsContainer);
